"use client"

import { useLayoutEffect, useRef } from "react"

/**
 * Canonical wavy breathing field rings for ThinkingVisual
 * - All 4 rings match PM-provided canonical geometry exactly
 * - Rings render conditionally (not opacity: 0) to prevent CSS resurrection
 * - Rings are bbox-centered to (1000, 1000) for perfect concentricity
 * - Shared stroke settings ensure no sharp artifacts
 * - SVG geometry remains static; animation targets wrapper only
 */
interface ThinkingFieldRingsSVGProps {
  className?: string
  ringCount?: 1 | 3 | 4 // Updated to support 3 rings
  outerRingBoost?: number
}

const TARGET_CX = 1000
const TARGET_CY = 1000

function centerPathToViewBox(pathEl: SVGPathElement | null) {
  if (!pathEl) return
  try {
    const bb = pathEl.getBBox()
    const cx = bb.x + bb.width / 2
    const cy = bb.y + bb.height / 2
    const dx = TARGET_CX - cx
    const dy = TARGET_CY - cy
    pathEl.setAttribute("transform", `translate(${dx} ${dy})`)
    console.log("[v0] Centered ring path:", {
      bbox: { cx, cy, width: bb.width, height: bb.height },
      offset: { dx, dy },
    })
  } catch (e) {
    console.warn("[v0] Failed to center path:", e)
  }
}

export function ThinkingFieldRingsSVG({ className, ringCount = 2, outerRingBoost }: ThinkingFieldRingsSVGProps) {
  const r1 = useRef<SVGPathElement>(null)
  const r2 = useRef<SVGPathElement>(null)
  const r3 = useRef<SVGPathElement>(null)
  const r4 = useRef<SVGPathElement>(null)

  useLayoutEffect(() => {
    centerPathToViewBox(r1.current)
    if (ringCount >= 2) centerPathToViewBox(r2.current)
    if (ringCount >= 3) centerPathToViewBox(r3.current)
    if (ringCount >= 4) centerPathToViewBox(r4.current)
  }, [ringCount])

  const ring1Opacity = 1.0 // Was 0.55 - now full brightness
  const ring2Opacity = 1.0 // Was 0.45 - now full brightness
  const ring3Opacity = 1.0 // Was 0.35 - now full brightness
  const ring4Opacity = 1.0 // Was 0.28 - now full brightness
  // </CHANGE>

  return (
    <svg
      viewBox="0 0 2000 2000"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      preserveAspectRatio="xMidYMid meet"
      style={{ transformOrigin: "50% 50%" }}
      data-field-rings="canonical"
    >
      <title>LogosIQ-Breathing-Field</title>
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        data-field="wavy-rings"
      >
        {/* Ring 1 - Always visible */}
        <path
          ref={r1}
          fill="none"
          opacity={ring1Opacity}
          d="M1151.964,1452.974c-27.655,0-54.423-12.369-73.465-35.595l-12.345-15.063c-16.509-20.146-40.752-31.7-66.497-31.7s-49.988,11.554-66.497,31.7l-12.345,15.063c-28.834,35.181-75.409,45.437-115.93,25.56-40.508-19.897-61.683-63.448-52.697-108.374l3.846-19.234c5.146-25.733-1.104-52.267-17.159-72.793-16.056-20.532-40.02-32.653-65.766-33.261l-19.238-.456c-44.943-1.057-81.869-31.804-91.871-76.508-10.002-44.705,10.178-88.746,50.225-109.582l17.146-8.923c22.929-11.941,39.37-33.468,45.099-59.07,5.729-25.602.074-52.274-15.507-73.179l-11.654-15.629c-27.208-36.5-26.66-85.107,1.375-120.957,28.034-35.844,74.379-47.198,115.321-28.274l17.525,8.101c23.45,10.836,50.212,10.532,73.404-.863,23.2-11.389,40.122-32.529,46.433-57.992l4.713-19.02c11.011-44.463,48.62-74.333,93.577-74.333s82.566,29.87,93.577,74.333l4.713,19.027c6.311,25.457,23.233,46.597,46.433,57.985,23.193,11.395,49.954,11.713,73.398.863l17.538-8.101c40.948-18.923,87.286-7.569,115.314,28.274,28.034,35.851,28.583,84.457,1.368,120.957l-11.64,15.629c-15.588,20.905-21.243,47.578-15.514,73.179,5.729,25.602,22.17,47.129,45.106,59.07l17.139,8.923c40.047,20.836,60.227,64.878,50.225,109.582-10.002,44.705-46.927,75.451-91.871,76.508l-19.238.456c-25.746.608-49.71,12.728-65.766,33.261-16.056,20.526-22.306,47.06-17.159,72.793l3.846,19.234c8.986,44.926-12.189,88.477-52.697,108.374-13.76,6.754-28.231,10.035-42.465,10.035Z"
          strokeWidth="44"
          data-ring="1"
        />

        {/* Ring 2 */}
        {ringCount >= 2 && (
          <path
            ref={r2}
            fill="none"
            opacity={ring2Opacity}
            d="M1187.727,1584.141c-41.479,0-81.627-18.535-110.185-53.334-19.34-23.579-47.729-37.096-77.885-37.096s-58.545,13.517-77.885,37.096c-43.253,52.719-113.148,68.109-173.889,38.288-60.754-29.808-92.515-95.071-79.035-162.399,6.035-30.106-1.293-61.159-20.093-85.181-18.801-24.023-46.878-38.215-77.028-38.923-67.413-1.582-122.788-47.655-137.795-114.645-15.007-66.99,15.267-132.988,75.332-164.219,26.856-13.974,46.111-39.168,52.822-69.129,6.711-29.954.091-61.165-18.164-85.625-40.81-54.698-39.985-127.54,2.059-181.258,42.044,53.718,111.555-70.764,172.966-42.379,27.473,12.696,58.805,12.326,85.973-1.013,27.168-13.332,46.988-38.069,54.375-67.858,16.52-66.62,72.928-111.382,140.361-111.382s123.841,44.762,140.361,111.382h0c7.386,29.788,27.207,54.526,54.375,67.858,27.187,13.339,58.513,13.703,85.973,1.013,61.417-28.385,130.928-11.339,172.966,42.379,42.045,53.718,42.87,126.56,2.059,181.258-18.255,24.459-24.875,55.671-18.164,85.625,6.711,29.96,25.966,55.155,52.822,69.129,60.066,31.231,90.339,97.229,75.332,164.219-15.007,66.99-70.382,113.063-137.795,114.645-30.15.708-58.227,14.901-77.028,38.923-18.801,24.023-26.128,55.075-20.093,85.181,13.48,67.328-18.281,132.591-79.035,162.399-20.646,10.135-42.356,15.04-63.704,15.046Z"
            strokeWidth="48"
            data-ring="2"
          />
        )}

        {/* Ring 3 */}
        {ringCount >= 3 && (
          <path
            ref={r3}
            fill="none"
            opacity={ring3Opacity}
            d="M1223.49,1715.307c-55.301,0-108.807-24.701-146.905-71.072-24.169-29.453-59.706-46.339-97.285-46.339s-73.116,16.886-97.285,46.339c-54.096,65.901-141.461,85.134-217.312,47.884-75.948-37.271-115.655-118.813-98.818-202.966,7.544-37.641-1.616-76.438-25.116-106.476-23.507-30.038-58.597-47.769-96.29-48.629-84.283-1.978-153.463-59.566-172.069-143.32-18.606-83.754,19.085-166.235,94.198-205.273,33.57-17.467,57.639-48.959,66.022-86.39,8.377-37.436.113-76.456-22.697-107.07-51.004-68.361-49.981-159.423,2.575-226.558,52.557-67.101,139.469-88.453,216.285-53.007,34.341,15.871,73.506,15.408,107.241-1.267,33.735-16.665,58.734-47.586,68.556-84.826,20.65-83.276,91.159-139.231,175.48-139.231s154.831,55.955,175.481,139.231c9.236,37.235,34.02,68.155,67.969,84.826,33.749,16.675,72.914,17.14,107.241,1.267,76.817-35.445,163.721-14.094,216.279,53.007,52.557,67.134,53.579,158.196,2.575,226.558-22.811,30.614-31.074,69.634-22.697,107.07,8.383,37.43,32.452,68.923,66.022,86.39,75.113,39.038,112.804,121.519,94.198,205.273-18.606,83.754-87.786,141.342-172.069,143.32-37.693.86-72.783,18.592-96.29,48.629-23.5,30.038-32.66,68.835-25.116,106.476,16.837,84.153-22.87,165.694-98.818,202.966-25.808,12.669-52.945,18.8-79.63,18.807Z"
            strokeWidth="53"
            data-ring="3"
          />
        )}

        {/* Ring 4 */}
        {ringCount >= 4 && (
          <path
            ref={r4}
            fill="none"
            opacity={ring4Opacity}
            d="M1259.253,1846.474c-69.126,0-135.987-30.868-183.625-88.816-30.213-36.816-74.639-57.91-121.328-57.91s-91.115,21.094-121.328,57.91c-67.616,82.354-176.77,106.398-271.545,59.822-94.935-46.565-144.49-148.462-123.468-253.61,9.43-47.034-2.019-95.557-31.396-133.095-29.383-37.538-73.246-59.696-120.362-60.783-105.354-2.473-191.816-74.447-214.956-179.095-23.141-104.648,23.859-207.782,117.758-256.554,41.962-21.833,72.033-61.179,82.472-107.989,10.439-46.804.141-95.581-28.307-133.846-63.752-85.432-62.472-199.253,3.218-283.559,65.696-84.312,174.401-110.592,270.481-66.298,42.921,19.833,91.877,19.255,134.103-1.587,42.226-20.83,73.405-59.469,85.644-106.021,25.813-104.097,113.966-174.079,219.377-174.079s193.565,69.982,219.378,174.079c11.542,46.552,42.533,85.191,84.953,106.021,42.212,20.841,91.169,21.422,134.103,1.587,96.08-44.294,204.778-18.014,270.474,66.298,65.696,84.306,66.976,198.127,3.218,283.559-28.514,38.265-38.748,87.042-28.307,133.846,10.439,46.81,40.51,86.156,82.472,107.989,93.899,48.772,140.899,151.906,117.758,256.554-23.141,104.648-109.602,176.622-214.956,179.095-47.116,1.087-90.979,23.245-120.362,60.783-29.377,37.538-40.826,86.061-31.396,133.095,21.022,105.148-28.533,207.045-123.468,253.61-32.26,15.836-66.181,23.5-99.537,23.507Z"
            strokeWidth="60"
            data-ring="4"
          />
        )}
      </g>
    </svg>
  )
}
